/**
 * ETP Featured Guide Slider – centered Swiper carousel.
 *
 *   - Active slide is centered and full size.
 *   - Neighbouring slides scale down and fade via slide.progress.
 *   - translateX compensates the scale-induced visual gap so the *visual*
 *     spacing between adjacent cards stays constant (= spaceBetween).
 *   - Edge fade to background is handled by a CSS mask gradient on .swiper.
 *   - Footer info panel follows the active real index.
 *   - External prev/next buttons wired to swiper.slidePrev/Next().
 */

export function initFeaturedGuideSlider(scope) {
  const wrappers = (scope || document).querySelectorAll(
    '.etp-guide-slider:not([data-etp-guide-ready])'
  );

  wrappers.forEach((wrapper) => {
    const el         = wrapper.querySelector('.swiper');
    const infoItems  = wrapper.querySelectorAll('.etp-guide-slider__info-item');
    const contentCount = infoItems.length;

    if (!window.Swiper || !el) {
      console.warn('[ETP guide-slider] window.Swiper or .swiper markup missing.');
      return;
    }

    // ── Scale + fade + gap-compensating translate ──────────────────────────
    //
    // scale(p)   = max(SCALE_MIN, 1 − SCALE_K · |p|)
    // opacity(p) = max(0.35,      1 − 0.80    · |p|)
    //
    // Without compensation, scaling shrinks slides inward and visually
    // increases the gap to the active card. translateX(p) pulls each scaled
    // slide back toward the active so the visual gap stays equal to Swiper's
    // spaceBetween at every rest state.
    //
    // Sign convention (Swiper, LTR, centeredSlides):
    //   slide.progress > 0  →  slide is visually LEFT of active  (pull right)
    //   slide.progress < 0  →  slide is visually RIGHT of active (pull left)
    // So tx = +sign(progress) · |compensation| pulls every slide TOWARD active.
    //
    // Magnitude derivation: for the visual gap between consecutive slides to
    // equal spaceBetween, we need at integer offsets
    //   |tx_n| − |tx_{n−1}| = W · (1 − (s_n + s_{n−1})/2).
    // SCALE_K · 1 > 1 − SCALE_MIN means scale saturates at |p| = 1, so
    // s_n = SCALE_MIN for every n ≥ 1 and s_0 = 1. That yields the closed form
    //   |p| ≤ 1 :  |tx| = W · ((1 − SCALE_MIN)/2) · |p|
    //   |p| > 1 :  |tx| = W · ((1 − SCALE_MIN)/2 + (1 − SCALE_MIN)·(|p|−1))

    const SCALE_MIN  = 0.60;
    const SCALE_K    = 0.55;
    const NEAR_SLOPE = (1 - SCALE_MIN) / 2;  // 0.20 → tx step for |p| ≤ 1
    const FAR_SLOPE  = 1 - SCALE_MIN;        // 0.40 → tx step per unit |p| beyond 1

    function applyEffect(swiper) {
      const slidesArr = swiper.slides;
      if (!slidesArr.length) return;

      // Read unscaled slide width once per call; CSS width is unaffected by
      // the transform applied below.
      const W = parseFloat(getComputedStyle(slidesArr[0]).width);
      if (!W || Number.isNaN(W)) return;

      slidesArr.forEach((slide) => {
        const p       = slide.progress;
        const abs     = Math.abs(p);
        const scale   = Math.max(SCALE_MIN, 1 - SCALE_K * abs);
        const opacity = Math.max(0.35, 1 - 0.80 * abs);
        const zIndex  = Math.round(10 - Math.min(abs * 12, 9));

        const txMag = abs <= 1
          ? NEAR_SLOPE * abs
          : NEAR_SLOPE + FAR_SLOPE * (abs - 1);
        const tx = Math.sign(p) * W * txMag;

        slide.style.transform = `translate3d(${tx}px, 0, 0) scale(${scale})`;
        slide.style.opacity   = String(opacity);
        slide.style.zIndex    = String(zIndex);
      });
    }

    function applyTransition(swiper, duration) {
      const t = duration > 0
        ? `transform ${duration}ms ease, opacity ${duration}ms ease`
        : '';
      swiper.slides.forEach((slide) => { slide.style.transition = t; });
    }

    // ── Info panel ─────────────────────────────────────────────────────────

    function showInfo(idx) {
      infoItems.forEach((item) => {
        item.classList.toggle('is-active', parseInt(item.dataset.index, 10) === idx);
      });
    }

    // ── Keyboard tabindex ──────────────────────────────────────────────────

    function updateTabindex() {
      el.querySelectorAll('.swiper-slide').forEach((slide) => {
        const link = slide.querySelector('.etp-guide-card');
        if (!link) return;
        link.setAttribute(
          'tabindex',
          slide.classList.contains('swiper-slide-active') ? '0' : '-1'
        );
      });
    }

    // ── Init ───────────────────────────────────────────────────────────────

    const swiper = new window.Swiper(el, {
      // Layout: slide width comes from CSS --etp-slide-w variable.
      // spaceBetween is the *target visual* gap; translateX compensates so
      // that the gap stays constant regardless of slide scale.
      slidesPerView:  'auto',
      centeredSlides: true,
      spaceBetween:   80,

      // Loop: slides × 2 satisfies Swiper 11's ceil(slidesPerView) × 2 requirement
      loop: true,

      // Required for slide.progress to be populated on every frame
      watchSlidesProgress: true,

      speed:      480,
      grabCursor: true,

      a11y: {
        enabled:          true,
        prevSlideMessage: 'Previous guide',
        nextSlideMessage: 'Next guide',
      },

      keyboard: {
        enabled:        true,
        onlyInViewport: true,
      },

      on: {
        init(sw) {
          applyEffect(sw);
          updateTabindex();
          showInfo(sw.realIndex % contentCount);
        },
        progress(sw) {
          applyEffect(sw);
        },
        setTransition(sw, duration) {
          applyTransition(sw, duration);
        },
        slideChange(sw) {
          showInfo(sw.realIndex % contentCount);
          updateTabindex();
        },
      },
    });

    // External navigation buttons
    const prevBtn = wrapper.querySelector('.etp-nav-arrow--prev');
    const nextBtn = wrapper.querySelector('.etp-nav-arrow--next');
    if (prevBtn) prevBtn.addEventListener('click', () => swiper.slidePrev());
    if (nextBtn) nextBtn.addEventListener('click', () => swiper.slideNext());

    wrapper.dataset.etpGuideReady = 'true';
  });
}
